import React, { useMemo } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getPolyglot } from '../../../i18n';
import { Report } from '../../../redux/reports/api/Report.model';
import { openReportDeleteModalAsPromise } from './DeleteReportModalPromise';

interface ReportMenuActionsProps {
  report: Report;
  onSelect?: (report: Report) => void;
}
export default function ReportMenuActions(props: ReportMenuActionsProps) {
  const polyglot = getPolyglot();
  const { report } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const options = useMemo(
    () => [
      {
        label: polyglot.t('reports.table.action.view'),
        href: undefined,
        onClick: () => {
          handleClose();
          if (props.onSelect) props.onSelect(report);
        },
      },
      ...Object.entries(report.files ?? {})
        .filter(([_, file]) => file)
        .map(([format, file]) => ({
          label: polyglot.t(`reports.table.action.download`, { format }),
          href: file,
          onClick: () => {
            handleClose();
          },
        })),
      {
        label: polyglot.t(`reports.table.action.delete`),
        href: undefined,
        onClick: async () => {
          try {
            await openReportDeleteModalAsPromise({ reportId: report.id });
          } catch (e) {
            // do nothing, user cancelled
          } finally {
            handleClose();
          }
        },
      },
    ],
    [polyglot, report]
  );

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          root: {
            'aria-labelledby': 'long-button',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.label}
            onClick={option.onClick}
            component={option.href ? 'a' : 'div'}
            href={option.href}
            target={option.href ? '_blank' : undefined}
            rel={option.href ? 'noopener noreferrer' : undefined}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
